import { useCallback, useEffect, useState } from 'react';
import type { UserInfo } from '@/types/user';
import { fetchMe } from './api';

export type ProfileErrorKey = 'profile.errorLoad';

interface Outcome {
  nonce: number;
  user: UserInfo | null;
  errorKey: ProfileErrorKey | null;
}

interface ProfileState {
  user: UserInfo | null;
  isLoading: boolean;
  errorKey: ProfileErrorKey | null;
  reload: () => void;
  /**
   * Adopts the account a save just returned.
   *
   * Without it the form keeps diffing against the values it loaded with, so every
   * later save re-sends fields that already hold what was sent — and "nothing has
   * changed" could never be true again. The write's own response is the newest
   * truth about this account, so there is nothing to re-fetch.
   */
  apply: (user: UserInfo) => void;
}

/**
 * The caller's own account, from the server rather than from the session.
 *
 * The session carries a `UserInfo` too, and seeding the form from it would save this
 * request — but that copy is frozen at the moment of login. A role changed since,
 * a company renamed, a phone number corrected by an administrator: all of them would
 * be edited back to their stale values by a form built on it, because this endpoint
 * applies exactly the fields it is sent. So the form is built on what the server says
 * now.
 *
 * There is no id in the request. The account comes from the token, which is what
 * makes this endpoint impossible to point at somebody else.
 *
 * **Loading is derived, not stored** — the outcome is tagged with the load it
 * answered, and loading is that tag not matching the load being asked for.
 */
export const useProfile = (): ProfileState => {
  const [nonce, setNonce] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetchMe(controller.signal)
      .then((user) => setOutcome({ nonce, user, errorKey: null }))
      .catch(() => {
        if (controller.signal.aborted) return;

        // No 403 branch: every signed-in role may read its own account, so a failure
        // here is a failure of the request rather than of permission. A 401 never
        // reaches this — the client ends the session and the guard routes to login.
        setOutcome({ nonce, user: null, errorKey: 'profile.errorLoad' });
      });

    return () => controller.abort();
  }, [nonce]);

  const isCurrent = outcome?.nonce === nonce;
  const reload = useCallback(() => setNonce((current) => current + 1), []);

  const apply = useCallback((user: UserInfo) => {
    setOutcome((current) => (current ? { ...current, user, errorKey: null } : current));
  }, []);

  return {
    user: outcome?.user ?? null,
    isLoading: !isCurrent,
    errorKey: isCurrent ? outcome.errorKey : null,
    reload,
    apply,
  };
};
